import React, { useCallback, useState, useEffect } from 'react'
import { makeStyles } from '@material-ui/styles'
import Text from 'core/elements/Text'
import SubmitButton from 'core/components/buttons/SubmitButton'
import { FormFieldCard } from 'core/components/validatedForm/FormFieldCard'
import { IconInfo } from 'core/components/validatedForm/Info'
import Theme from 'core/themes/model'
import { routes } from 'core/utils/routes'
import BulletList from 'core/components/BulletList'
import { CloudProviders } from 'app/plugins/infrastructure/components/cloudProviders/model'
import GoogleCloudProviderRequirementDialog from '../google/GoogleCloudProviderRequirementDialog'
import useScopedPreferences from 'core/session/useScopedPreferences'
import { isServiceTenant } from 'k8s/util/helpers'
import { ErrorMessage } from 'core/components/validatedForm/ErrorMessage'
import { userTenantsSelector } from 'account/components/userManagement/tenants/selectors'
import { useSelector } from 'react-redux'
import useListAction from 'core/hooks/useListAction'
import { listCloudProviders } from 'app/plugins/infrastructure/components/cloudProviders/new-actions'
import { cloudProvidersSelector } from 'app/plugins/infrastructure/components/cloudProviders/selectors'

const useStyles = makeStyles<Theme>((theme) => ({
  requirements: {
    display: 'flex',
    flexFlow: 'row nowrap',
    justifyContent: 'space-between',
    margin: theme.spacing(2, 4, 1, 4),
  },
  indented: {
    margin: theme.spacing(0, 4),
  },
  text: {
    marginTop: theme.spacing(0.5),
    marginLeft: theme.spacing(3),
  },
  bulletList: {
    marginLeft: theme.spacing(2),
    flex: 1,
  },
  actionRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },
  formCard: {
    color: theme.palette.grey[700],
  },
}))

const GoogleReqs = [
  'Compute Viewer: roles/compute.viewer',
  'Project Viewer: roles/viewer',
  'Kubernetes Engine Admin: roles/container.admin',
  'Service Account User: roles/iam.serviceAccountUser',
]

const ImportGKERequirements = ({ onComplete, platform }) => {
  const classes = useStyles({})
  const [showDialog, setShowDialog] = useState(false)
  const [errorMessage, setErrorMessage] = useState(null)
  const { loading } = useListAction(listCloudProviders)
  const cloudProviders = useSelector(cloudProvidersSelector)
  const userTenants = useSelector(userTenantsSelector)
  const { prefs } = useScopedPreferences()
  const { currentTenant } = prefs

  useEffect(() => {
    setErrorMessage(null)
  }, [currentTenant])

  const handleClick = useCallback(() => {
    if (!isServiceTenant(userTenants, currentTenant)) {
      setErrorMessage('Only authorized for service tenant')
      return
    }
    const hasGoogleProvider = !!cloudProviders.some(
      (provider) => provider.type === CloudProviders.Gcp,
    )
    if (!hasGoogleProvider) {
      setShowDialog(true)
    } else {
      onComplete(routes.cluster.import.gke.path())
    }
  }, [onComplete, cloudProviders, userTenants, currentTenant])

  return (
    <>
      {showDialog && (
        <GoogleCloudProviderRequirementDialog
          showDialog={showDialog}
          setShowDialog={setShowDialog}
        />
      )}
      <FormFieldCard
        className={classes.formCard}
        title="Google GKE Cluster Management"
        // link={
        //   <ExternalLink textVariant="caption2" url={gkeHelpLink}>
        //     GKE Help
        //   </ExternalLink>
        // }
      >
        <Text variant="body2" className={classes.text}>
          Platform9 is able to connect to Google and import existing GKE clusters to bring them
          under management. Once connected to Platform9 you will be able to deploy Platform9
          Monitoring, leverage the Application Catalog to deploy apps and configure RBAC, Pods,
          Deployments and Services.
        </Text>
        <IconInfo
          icon="info-circle"
          title="The following requirements must be met to be able to import and manage GKE Clusters:"
        >
          <Text variant="body2" className={classes.indented}>
            A service account within your Google Cloud project with the following roles:
          </Text>
          <div className={classes.requirements}>
            <BulletList className={classes.bulletList} items={GoogleReqs} />
          </div>
        </IconInfo>
        <div className={classes.actionRow}>
          <SubmitButton onClick={handleClick}>Import GKE Clusters</SubmitButton>
        </div>
        <ErrorMessage>{errorMessage}</ErrorMessage>
      </FormFieldCard>
    </>
  )
}
export default ImportGKERequirements
